import { HttpEvent, HttpHeaders, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable, throwError } from 'rxjs'
import { $app } from '../../../app.service'
import {
   Todo,
   TodoNotificationRequest,
   TodoPartnerStatusUpdate,
   TodoTableData
} from '../../../features/feature-partners/models/interfaces/table-todo-data.interface'
import { AppBasicPartner } from '../../models/interfaces/app-basic-partner.interface'
import { AppBasicPerson } from '../../models/interfaces/app-basic-person.interface'
import { AppBasicService } from '../../services/app-basic.service'
import { AppBasicDocument, AppBasicDocumentRes } from '../../models/interfaces/app-basic-document.interface'
import { AppEditProjectRequest } from '../../models/interfaces/app-edit-project-request.interface'
import { AppProject } from '../../models/interfaces/app-project.interface'
import { AppReportingPeriodRequest, AppReportingPeriodResponse } from '../../models/interfaces/app-reporting-period.interface'

@Injectable()
export class ProjectService extends AppBasicService {
   getProjectById(projectId: number): Observable<AppProject> {
      return this.http.get<AppProject>(`${this._configService.apiUrl}/projects/${projectId}`)
   }

   updateProjectDetail(request: AppEditProjectRequest): Observable<void> {
      const projectId = $app.getCurrentProjectId()
      return this.http.patch<void>(`${this._configService.apiUrl}/projects/${projectId}`, request)
   }

   exportProject(): Observable<HttpEvent<Blob>> {
      if (this.console) console.log(this.logPrefix, 'exportProject')

      const projectId = $app.getCurrentProjectId()

      const httpOptions = {
         headers: new HttpHeaders({
            'Content-Type': 'application/json'
         }),
         reportProgress: true,
         observe: 'events' as const,
         responseType: 'blob' as const
      }

      return this.http.get(`${this._configService.apiUrl}/workplans/projects/${projectId}/exportMsWordFile`, httpOptions)
   }

   getPartnerList(): Observable<AppBasicPartner[]> {
      const projectId = $app.getCurrentProjectId()

      return this.http.get<AppBasicPartner[]>(`${this._configService.apiUrl}/projects/${projectId}/partners`)
   }

   getPartnerById(id: number): Observable<AppBasicPartner> {
      const projectId = $app.getCurrentProjectId()
      return this.http.get<AppBasicPartner>(`${this._configService.apiUrl}/projects/${projectId}/partners/${id}`)
   }

   insertPartner(data: Partial<AppBasicPartner>): Observable<void> {
      const projectId = $app.getCurrentProjectId()

      return this.http.post<void>(`${this._configService.apiUrl}/projects/${projectId}/partners`, data, { headers: this.httpOptions.headers })
   }

   updatePartner(id: number, data: Partial<AppBasicPartner>): Observable<void> {
      return this.http.put<void>(`${this._configService.apiUrl}/projects/partners/${id}`, data, { headers: this.httpOptions.headers })
   }

   changePartnerOrder(data: number[]): Observable<AppBasicPartner[]> {
      const projectId = $app.getCurrentProjectId()
      const params: { sortedPartnersId: number[] } = { sortedPartnersId: data }

      return this.http.post<AppBasicPartner[]>(`${this._configService.apiUrl}/projects/${projectId}/partners:sort`, params)
   }

   deletePartner(id: number): Observable<void> {
      const projectId = $app.getCurrentProjectId()

      return this.http.delete<void>(`${this._configService.apiUrl}/projects/${projectId}/partners/${id}`, {
         headers: this.httpOptions.headers
      })
   }

   getPersonById(id: number): Observable<AppBasicPerson> {
      return this.http.get<AppBasicPerson>(`${this._configService.apiUrl}/projects/persons/${id}`)
   }

   searchPersonsByEmail(email: string): Observable<AppBasicPerson[]> {
      const projectId = $app.getCurrentProjectId()
      const params = new HttpParams().set('email', email)

      return this.http.get<AppBasicPerson[]>(`${this._configService.apiUrl}/projects/${projectId}/persons:search`, { params })
   }

   insertPerson(partnerId: number, data: Partial<AppBasicPerson>): Observable<void> {
      return this.http.post<void>(`${this._configService.apiUrl}/projects/partners/${partnerId}/persons`, data, { headers: this.httpOptions.headers })
   }

   updatePerson(partnerId: number, id: number, data: Partial<AppBasicPerson>): Observable<void> {
      return this.http.put<void>(`${this._configService.apiUrl}/projects/persons/${id}`, data, { headers: this.httpOptions.headers })
   }

   deletePerson(partnerId: number, id: number): Observable<void> {
      return this.http.delete<void>(`${this._configService.apiUrl}/projects/partners/${partnerId}/persons/${id}`, {
         headers: this.httpOptions.headers
      })
   }

   getProjectTodo(): Observable<TodoTableData> {
      if (this.console) console.log(this.logPrefix, 'getProjectTodo')

      const projectId = $app.getCurrentProjectId()

      return this.http.get<TodoTableData>(`${this._configService.apiUrl}/todo-managers/${projectId}`)
   }

   getTodoById(id: number): Observable<Todo> {
      const projectId = $app.getCurrentProjectId()

      return this.http.get<Todo>(`${this._configService.apiUrl}/todo-managers/${projectId}/todos/${id}`)
   }

   insertTodo(data: Todo): Observable<void> {
      const projectId = $app.getCurrentProjectId()
      const params: Todo = { ...data, managerId: projectId }

      return this.http.post<void>(`${this._configService.apiUrl}/todo-managers/${projectId}/todos`, params, {
         headers: this.httpOptions.headers
      })
   }

   updateTodo(id: number, data: Todo): Observable<void> {
      const projectId = $app.getCurrentProjectId()

      return this.http.put<void>(`${this._configService.apiUrl}/todo-managers/${projectId}/todos/${id}`, data, {
         headers: this.httpOptions.headers
      })
   }

   deleteTodo(id: number): Observable<void> {
      const projectId = $app.getCurrentProjectId()

      return this.http.delete<void>(`${this._configService.apiUrl}/todo-managers/${projectId}/todos/${id}`, {
         headers: this.httpOptions.headers
      })
   }

   updateTodoPartnerStatus(id: number, data: TodoPartnerStatusUpdate): Observable<void> {
      const projectId = $app.getCurrentProjectId()
      return this.http.post<void>(`${this._configService.apiUrl}/todo-managers/${projectId}/todos/${id}:change-status`, data, {
         headers: this.httpOptions.headers
      })
   }

   sendTodoNotification(id: number): Observable<void> {
      const projectId = $app.getCurrentProjectId()
      if (projectId == null) return throwError(() => new Error('Current project id is required'))

      const params: TodoNotificationRequest = {
         todoId: id,
         managerId: projectId
      }

      return this.http.post<void>(`${this._configService.apiUrl}/todo-managers/${projectId}/todos/notification:send`, params, {
         headers: this.httpOptions.headers
      })
   }

   getReportingPeriods(): Observable<AppReportingPeriodResponse> {
      const projectId = $app.getCurrentProjectId()
      return this.http.get<AppReportingPeriodResponse>(`${this._configService.newApiUrl}/projects/${projectId}/reporting-periods`)
   }

   deleteReportingPeriod(): Observable<void> {
      const projectId = $app.getCurrentProjectId()
      return this.http.delete<void>(`${this._configService.apiUrl}/projects/${projectId}/reporting-periods/last`)
   }

   addReportingPeriod(request: AppReportingPeriodRequest): Observable<void> {
      const projectId = $app.getCurrentProjectId()
      return this.http.post<void>(`${this._configService.apiUrl}/projects/${projectId}/reporting-periods`, request)
   }

   linkPartnerToCRM(partnerId: number, organizationId: number): Observable<void> {
      const projectId = $app.getCurrentProjectId()
      const request: { organizationId: number } = { organizationId }
      return this.http.post<void>(`${this._configService.newApiUrl}/projects/${projectId}/partners/${partnerId}:link-organisation`, request)
   }

   unlinkPartnerFromCRM(partnerId: number): Observable<void> {
      const projectId = $app.getCurrentProjectId()
      return this.http.post<void>(`${this._configService.newApiUrl}/projects/${projectId}/partners/${partnerId}:unlink-organisation`, null)
   }

   getLogo(): Observable<Blob> {
      const projectId = $app.getCurrentProjectId()
      return this.http.get(`${this._configService.newApiUrl}/projects/${projectId}/logo`, { responseType: 'blob' })
   }

   uploadLogo(base64Logo: string): Observable<void> {
      const projectId = $app.getCurrentProjectId()
      const request: { base64Logo: string } = { base64Logo }

      return this.http.post<void>(`${this._configService.newApiUrl}/projects/${projectId}/logo`, request)
   }

   deleteLogo(): Observable<void> {
      const projectId = $app.getCurrentProjectId()
      return this.http.delete<void>(`${this._configService.newApiUrl}/projects/${projectId}/logo`)
   }

   getDocuments(): Observable<AppBasicDocumentRes> {
      const projectId = $app.getCurrentProjectId()
      return this.http.get<AppBasicDocumentRes>(`${this._configService.apiUrl}/projects/${projectId}/quick-links`)
   }

   addDocument(document: AppBasicDocument): Observable<void> {
      const projectId = $app.getCurrentProjectId()
      return this.http.post<void>(`${this._configService.apiUrl}/projects/${projectId}/quick-links`, document)
   }

   deleteDocument(documentId: number): Observable<void> {
      const projectId = $app.getCurrentProjectId()
      return this.http.delete<void>(`${this._configService.apiUrl}/projects/${projectId}/quick-links/${documentId}`)
   }

   updateDocument(document: AppBasicDocument): Observable<void> {
      const projectId = $app.getCurrentProjectId()
      return this.http.put<void>(`${this._configService.apiUrl}/projects/${projectId}/quick-links/${document.id}`, document)
   }

   sendTodoNotificationToPerson(partnerId: number, personId: number): Observable<void> {
      const projectId = $app.getCurrentProjectId()
      const request: { partnerId: number; personId: number; projectId?: number } = {
         partnerId,
         personId,
         projectId
      }
      return this.http.post<void>(`${this._configService.apiUrl}/projects/persons/send-invitation`, request)
   }
}
